import { ExclamationIcon } from '@/components/common/icon';
import i18n from '@/i18n';
import { getUserLang } from '@/i18n/utils';
import { Tooltip } from 'aha-components';

const { t } = i18n;

// i18N-TODO: 用的 toLocaleXXX API

export const getTimezoneName = (
  timestamp: string | Date | null | undefined
) => {
  // 检查 null 或 undefined
  if (!timestamp) {
    return '';
  }

  // console.log(timestamp);
  const today = new Date(timestamp);

  // 检查是否为有效日期
  if (isNaN(today.getTime())) {
    return '';
  }
  const short = today.toLocaleDateString(getUserLang().detectedValue);
  const full = today.toLocaleDateString(getUserLang().detectedValue, {
    timeZoneName: 'long'
  });

  // Trying to remove date from the string in a locale-agnostic way
  const shortIndex = full.indexOf(short);
  if (shortIndex >= 0) {
    const trimmed =
      full.substring(0, shortIndex) + full.substring(shortIndex + short.length);

    // by this time `trimmed` should be the timezone's name with some punctuation -
    // trim it from both sides
    return trimmed.replace(/^[\s,.\-:;]+|[\s,.\-:;]+$/g, '');
  } else {
    // in some magic case when short representation of date is not present in the long one, just return the long one as a fallback, since it should contain the timezone's name
    return full;
  }
};

export const formatDateWithSuffix = (
  time: string | Date | null | undefined
) => {
  // 检查 null 或 undefined
  if (!time) {
    return '-';
  }

  const date = new Date(time);

  // 检查是否为有效日期
  if (isNaN(date.getTime())) {
    return '-';
  }

  const day = date.getDate();
  // 获取日期的数字部分和序数后缀
  let suffix = 'th'; // 默认后缀
  if (day % 10 === 1 && day !== 11) {
    suffix = 'st';
  } else if (day % 10 === 2 && day !== 12) {
    suffix = 'nd';
  } else if (day % 10 === 3 && day !== 13) {
    suffix = 'rd';
  }

  // 使用 toLocaleDateString 获取格式化日期
  const formattedDate = date.toLocaleDateString(getUserLang().detectedValue, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // 使用 toLocaleTimeString 获取格式化时间
  const formattedTime = date.toLocaleTimeString(getUserLang().detectedValue, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // 格式化日期部分，替换数字日期部分，添加序数后缀
  const formattedDateWithSuffix = formattedDate.replace(
    /\d+/,
    (match) => match + suffix
  );

  // 合并日期和时间部分
  return `${formattedDateWithSuffix} ${formattedTime}`;
};

export const getTime = (timestamp: string | Date | null | undefined) => {
  // 检查 null 或 undefined
  if (!timestamp) {
    return '-';
  }

  const today = new Date(timestamp);

  // 检查是否为有效日期
  if (isNaN(today.getTime())) {
    return '-';
  }

  const time = today
    .toLocaleString(getUserLang().detectedValue, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    .replace(',', '')
    .replace(/(\d+)\/(\d+)\/(\d+)(.*)/, '$2/$1/$3 at$4');

  return time;
  // 输出格式: '05/10/2024 at 9:00 AM'
};

export const getDuration = (hours: number) => {
  // if (hours > 48) return `${Math.ceil(hours / 24)} days`;
  // if (hours > 1) return `${Math.ceil(hours)} hours`;
  // return `${Math.ceil(hours)} hour`;
  if (hours === 24)
    return `1 ${t('common.units.day', { defaultValue: 'day' })}`;
  if (hours > 24)
    return `${Math.ceil(hours / 24)} ${t('common.units.days', { defaultValue: 'days' })}`;
  if (hours > 1)
    return `${hours} ${t('common.units.hours', { defaultValue: 'hours' })}`;
  return `${Math.ceil(hours / 24)} ${t('common.units.day', { defaultValue: 'day' })}`;
};

export const getDurationFormatBusinessDays = (hours: number) => {
  if (hours === 24)
    return `1 ${t('common.units.business_day', { defaultValue: 'business day' })}`;
  if (hours > 24)
    return `${Math.ceil(hours / 24)} ${t('common.units.business_days', { defaultValue: 'business days' })}`;
  if (hours > 1)
    return `${hours} ${t('common.units.hours', { defaultValue: 'hours' })}`;
  return `${Math.ceil(hours / 24)} ${t('common.units.business_days', { defaultValue: 'business days' })}`;
};

// 使用 toLocaleDateString 获取格式化日期
export const formattedDate = (
  time: string | Date,
  showTime: boolean = true,
  showSeconds: boolean = false
) => {
  const date = new Date(time);
  if (isNaN(date.getTime()) || !time) return '-';
  const lang = getUserLang().detectedValue;
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(showTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          ...(showSeconds ? { second: '2-digit' } : {})
        }
      : {})
  };
  return new Intl.DateTimeFormat(lang, options).format(date);
};

export const getDeadlineTime = (deadline: string | Date, t: any) => {
  const nowMs = Date.now();
  const deadlineMs = new Date(deadline).getTime();
  const diffMs = deadlineMs - nowMs;
  const hourMs = 60 * 60 * 1000;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;

  if (diffMs <= 0) {
    // 已逾期：根据逾期时长显示不同颗粒度的文案
    const overdueMs = -diffMs;
    const overdueDaysFloor = Math.floor(overdueMs / dayMs); // tooltip 文案使用“天”

    let overdueText: string = t('todaysActions.overdue', {
      defaultValue: 'Overdue'
    });

    if (overdueMs >= hourMs && overdueMs < dayMs) {
      const hours = Math.floor(overdueMs / hourMs);
      overdueText = t('todaysActions.overdueInHours', {
        hours,
        defaultValue: '{{hours}} hour(s) overdue'
      });
    } else if (overdueMs >= dayMs && overdueMs < 14 * dayMs) {
      const days = Math.floor(overdueMs / dayMs);
      overdueText = t('todaysActions.overdueInDays', {
        days,
        defaultValue: '{{days}} day(s) overdue'
      });
    } else if (overdueMs >= 14 * dayMs) {
      const weeks = Math.floor(overdueMs / weekMs);
      overdueText = t('todaysActions.overdueInWeeks', {
        weeks,
        defaultValue: '{{weeks}} week(s) overdue'
      });
    }

    return (
      <Tooltip
        content={t('todaysActions.overdueTooltip', {
          defaultValue:
            'The brand is excited to see your content! Please submit it soon or extend the timeline. Continued delays may affect your platform credits and future brand opportunities.'
        })}
      >
        <div className="flex items-center gap-2">
          <div className="justify-start text-[#D92D20] text-[12px] md:text-sm font-medium font-['DM_Sans'] leading-5">
            {overdueText}
          </div>
          <ExclamationIcon className="w-4 h-4 text-[#D92D20]" />
        </div>
      </Tooltip>
    );
  }

  // 距离当前不足一天（未逾期）
  if (diffMs < dayMs) {
    const hours = Math.ceil(diffMs / hourMs);
    return (
      <Tooltip
        content={t('todaysActions.dueInOneDaysTooltip', {
          defaultValue:
            'The brand is really excited to see your work! Submitting on time will help boost your credibility and unlock more collaboration opportunities.'
        })}
      >
        <div className="flex items-center gap-2">
          <div className="justify-start text-[#D92D20] text-[12px] md:text-sm font-medium font-['DM_Sans'] leading-5">
            {t('todaysActions.dueInXHours', {
              hours,
              defaultValue: 'Due in {{hours}} hour(s)'
            })}
          </div>
          <ExclamationIcon className="w-4 h-4 text-[#D92D20]" />
        </div>
      </Tooltip>
    );
  }

  return formattedDate(deadline, false);
};
